import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import type { Theme } from '@mui/material/styles';
import { usePageContentTop } from '@src/app/layout';
import {
  SaveShortcutBoundary,
  type SaveShortcutStrategy,
} from '@src/shared/ui/SaveShortcutScope';
import type { PropsWithChildren, ReactNode } from 'react';
import SidePanelContent from './SidePanelContent';

type SidePanelProps = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  hideCloseButton?: boolean;
  disableBackdropClose?: boolean;
  shortcutBoundary?: boolean;
  shortcutStrategy?: SaveShortcutStrategy;
}>;

const StyleSheet = {
  paper: (contentTop: number) => (theme: Theme) => ({
    border: '1px solid',
    borderColor: 'grey.100',
    borderRadius: 2,
    overflow: 'hidden',
    // `position: fixed` (from the MUI Drawer paper) + top + bottom fully define
    // the height; `height: auto` just overrides the paper's default `height: 100%`.
    top: `${contentTop}px`,
    bottom: `${theme.layout.content.bottomPadding}px`,
    height: 'auto',
    // Align the panel's right edge with the page content's right padding.
    right: `${theme.layout.content.rightPadding}px`,
    width: theme.layout.aside.width,
  }),
};

function SidePanel({
  open,
  onClose,
  title,
  footer,
  hideCloseButton = false,
  disableBackdropClose = false,
  shortcutBoundary = true,
  shortcutStrategy = 'active',
  children,
}: SidePanelProps) {
  // Every SidePanel sits at the same height — right below the page header —
  // which the shared Page layout measures for us.
  const contentTop = usePageContentTop();

  const handleClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (disableBackdropClose && reason === 'backdropClick') {
      return;
    }

    onClose();
  };

  const drawer = (
    <Drawer
      anchor="right"
      elevation={0}
      open={open}
      onClose={handleClose}
      variant="temporary"
      ModalProps={{
        disablePortal: true,
      }}
      slots={{
        transition: Fade,
      }}
      slotProps={{
        paper: {
          sx: StyleSheet.paper(contentTop),
        },
        transition: {
          timeout: {
            enter: 180,
            exit: 120,
          },
        },
      }}
    >
      <SidePanelContent
        title={title}
        footer={footer}
        onClose={onClose}
        hideCloseButton={hideCloseButton}
      >
        {children}
      </SidePanelContent>
    </Drawer>
  );

  if (!shortcutBoundary) {
    return drawer;
  }

  return (
    <SaveShortcutBoundary
      active={open}
      priority="modal"
      strategy={shortcutStrategy}
    >
      {drawer}
    </SaveShortcutBoundary>
  );
}

export default SidePanel;
